import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../environments/environment';
import {
  PublicAvailability,
  PublicAvailabilityFilterOptions,
  PublicAvailabilitySearchParams,
  PublicCompanyDirectoryEntry,
  PublicCompanyDirectorySearchParams,
  PublicLoad,
  PublicLoadFilterOptions,
  PublicLoadSearchParams,
  PublicPage
} from './public-marketplace.models';

@Injectable({ providedIn: 'root' })
export class PublicMarketplaceService {
  private readonly http = inject(HttpClient);
  private readonly baseUrl = `${environment.aiServiceApiUrl}public/marketplace`;

  getLoads(params: PublicLoadSearchParams): Observable<PublicPage<PublicLoad>> {
    return this.http
      .get<unknown>(`${this.baseUrl}/loads`, {
        params: this.toHttpParams(params)
      })
      .pipe(map(response => this.normalizePage<PublicLoad>(response, params.page, params.size)));
  }

  getLoad(id: number, lang?: string): Observable<PublicLoad> {
    return this.http
      .get<unknown>(`${this.baseUrl}/loads/${id}`, {
        params: this.toHttpParams({ lang })
      })
      .pipe(map(response => this.normalizeEntity<PublicLoad>(response)));
  }

  recordLoadView(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/loads/${id}/view`, null);
  }

  getLoadFilterOptions(params: {
    origin?: string;
    destination?: string;
    equipmentType?: string;
    paymentTerms?: string;
    commodity?: string;
    lang?: string;
  } = {}): Observable<PublicLoadFilterOptions> {
    return this.http
      .get<unknown>(`${this.baseUrl}/loads/filter-options`, {
        params: this.toHttpParams(params)
      })
      .pipe(map(response => this.normalizeEntity<PublicLoadFilterOptions>(response)));
  }

  getAvailabilities(
    params: PublicAvailabilitySearchParams
  ): Observable<PublicPage<PublicAvailability>> {
    return this.http
      .get<unknown>(`${this.baseUrl}/availabilities`, {
        params: this.toHttpParams(params)
      })
      .pipe(
        map(response =>
          this.normalizePage<PublicAvailability>(response, params.page, params.size)
        )
      );
  }

  getAvailability(id: number, lang?: string): Observable<PublicAvailability> {
    return this.http
      .get<unknown>(`${this.baseUrl}/availabilities/${id}`, {
        params: this.toHttpParams({ lang })
      })
      .pipe(map(response => this.normalizeEntity<PublicAvailability>(response)));
  }

  recordAvailabilityView(id: number): Observable<void> {
    return this.http.post<void>(`${this.baseUrl}/availabilities/${id}/view`, null);
  }

  getAvailabilityFilterOptions(params: { corridorOrigin?: string; corridorDestination?: string; equipmentType?: string; lang?: string } = {}): Observable<PublicAvailabilityFilterOptions> {
    return this.http
      .get<unknown>(`${this.baseUrl}/availabilities/filter-options`, {
        params: this.toHttpParams(params)
      })
      .pipe(map(response => this.normalizeEntity<PublicAvailabilityFilterOptions>(response)));
  }

  getDirectoryCompanies(
    params: PublicCompanyDirectorySearchParams
  ): Observable<PublicPage<PublicCompanyDirectoryEntry>> {
    return this.http
      .get<unknown>(`${this.baseUrl}/directory/companies/search`, {
        params: this.toHttpParams(params)
      })
      .pipe(
        map(response =>
          this.normalizePage<PublicCompanyDirectoryEntry>(response, params.page, params.size)
        )
      );
  }

  getDirectoryCompany(id: number): Observable<PublicCompanyDirectoryEntry> {
    return this.http
      .get<unknown>(`${this.baseUrl}/directory/companies/${id}`)
      .pipe(map(response => this.normalizeEntity<PublicCompanyDirectoryEntry>(response)));
  }

  private toHttpParams(params: object): HttpParams {
    let httpParams = new HttpParams();
    Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
      if (value === null || value === undefined || value === '') {
        return;
      }
      httpParams = httpParams.set(key, String(value));
    });
    return httpParams;
  }

  private normalizePage<T>(response: unknown, requestedPage = 0, requestedSize = 12): PublicPage<T> {
    const unwrapped = this.unwrap(response);

    if (Array.isArray(unwrapped)) {
      return {
        content: unwrapped as T[],
        totalElements: unwrapped.length,
        totalPages: unwrapped.length > 0 ? 1 : 0,
        size: requestedSize,
        number: requestedPage
      };
    }

    const record = this.asRecord(unwrapped);
    const content = this.firstArray<T>(
      record['content'],
      record['items'],
      record['records'],
      record['results'],
      record['loads'],
      record['availabilities'],
      record['companies'],
      record['loadRequests'],
      record['loadAvailabilities'],
      record['data']
    );
    const totalElements = this.firstNumber(
      record['totalElements'],
      record['totalCount'],
      record['total'],
      content.length
    );
    const size = this.firstNumber(record['size'], record['pageSize'], requestedSize);
    const number = this.firstNumber(record['number'], record['page'], requestedPage);

    return {
      content,
      totalElements,
      totalPages: this.firstNumber(
        record['totalPages'],
        size > 0 ? Math.ceil(totalElements / size) : 0
      ),
      size,
      number
    };
  }

  private normalizeEntity<T>(response: unknown): T {
    return this.unwrap(response) as T;
  }

  private unwrap(response: unknown): unknown {
    let current = response;
    for (let index = 0; index < 3; index += 1) {
      const record = this.asRecord(current);
      if ('data' in record && !('content' in record) && !Array.isArray(current)) {
        current = record['data'];
        continue;
      }
      return current;
    }
    return current;
  }

  private firstArray<T>(...values: unknown[]): T[] {
    for (const value of values) {
      const unwrapped = this.unwrap(value);
      if (Array.isArray(unwrapped)) {
        return unwrapped as T[];
      }
      const record = this.asRecord(unwrapped);
      if (Array.isArray(record['content'])) {
        return record['content'] as T[];
      }
      if (Array.isArray(record['loads'])) {
        return record['loads'] as T[];
      }
      if (Array.isArray(record['availabilities'])) {
        return record['availabilities'] as T[];
      }
      if (Array.isArray(record['companies'])) {
        return record['companies'] as T[];
      }
    }
    return [];
  }

  private firstNumber(...values: unknown[]): number {
    for (const value of values) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
    return 0;
  }

  private asRecord(value: unknown): Record<string, unknown> {
    return value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  }
}
